import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { DonorJourneySection } from "../models/donor-journey-section.model";
import { Observable, forkJoin, of, tap, Subject } from "rxjs";
import { DonorProfile, ProfileInfo } from "../models/donorProfile.model";
import { Appointment } from "../models/appointment.model";
import { S3Document } from "../models/s3-document.model";
import { Contract } from "../models/contract.model";
import { environment } from "../../environments/environment";

export interface TranslationRequest {
  sourceLocale?: string;
  targetLocale: string;
  text: string;
  context?: string;
}

export interface TranslationResponse {
  translatedText: string;
  sourceLocale?: string;
  targetLocale: string;
}

@Injectable({
  providedIn: "root",
})
export class ContentService {
  private baseUrl = environment.contentApiUrl;
  // private baseUrl = 'http://localhost:3003/api';
  private donorSectionsCacheKey = "donorJourneySectionsCache";
  private donorSectionsCacheDuration = 8 * 60 * 60 * 1000; // 8 hours in ms

  private donorJourneyUserCacheKey = "donorJourneySectionsCache";
  private donorJourneyUserCacheDuration = 5 * 60 * 1000;
  private inMemoryJourneySections: DonorJourneySection[] | null = null;

  // private donorSectionsForUserCacheKey = "userDonorJourneySectionsCache";
  // private profileCacheKey = "donorAndRecipientProfileCache";

  private donorProfileCacheKey = "donorProfileCache";
  private donorProfileCacheDuration = 5 * 60 * 1000; // 5 minutes in ms

  private profileUpdatedSource = new Subject<void>();
  profileUpdated$ = this.profileUpdatedSource.asObservable();

  constructor(private http: HttpClient) {}

  // Fetch donor journey sections with caching
  getDonorJourneySections(): Observable<DonorJourneySection[]> {
    const cachedData = this.getCachedData(this.donorSectionsCacheKey);

    if (cachedData) {
      return of(cachedData);
    }

    const url = `${this.baseUrl}/journey/journey-sections`;

    return this.http.get<DonorJourneySection[]>(url).pipe(
      tap((response) => {
        this.cacheData(
          response,
          this.donorSectionsCacheDuration,
          this.donorSectionsCacheKey
        );
      })
    );
  }

  getDonorJourneySectionStatusForUser(): Observable<DonorJourneySection[]> {
    const url = `${this.baseUrl}/journey/user/journey-sections`;
    return this.http.get<DonorJourneySection[]>(url);
    // );
    // if (this.inMemoryJourneySections) {
    //   return of(this.inMemoryJourneySections);
    // }

    // // Check localStorage cache
    // const cached = this.getCachedData(this.donorJourneyUserCacheKey);
    // if (cached) {
    //   this.inMemoryJourneySections = cached;
    //   return of(cached);
    // }

    // const url = `${this.baseUrl}/journey/user/journey-sections`;
    // return this.http.get<DonorJourneySection[]>(url, { withCredentials: true }).pipe(
    //   tap((data) => {
    //     this.inMemoryJourneySections = data;
    //     this.cacheData(data, this.donorJourneyUserCacheDuration, this.donorJourneyUserCacheKey);
    //   })
    // );
  }

  initializeJourneySectionsForUser(): Observable<any> {
    const url = `${this.baseUrl}/journey/user/journey-sections/initialize`;
    return this.http.post(url, { withCredentials: true });
  }

  updateQuestionnaireIdOnJourneySectionForUser(
    sectionId: string,
    questionnaireId: string
  ): Observable<any> {
    const url = `${this.baseUrl}/journey/user/journey-sections/questionnaire`;
    const patchObject = { sectionId, questionnaireId };

    return this.http.patch(url, patchObject, { withCredentials: true });
  }

  updateJourneySectionStatusForUser(
    sectionId: string,
    status: string
  ): Observable<any> {
    const url = `${this.baseUrl}/journey/user/journey-sections/status`;
    const postObject = { sectionId, status };

    return this.http.post(url, postObject, { withCredentials: true });
  }

  updateJourneySectionStatusByUserIdForAdmin(
    sectionId: string,
    status: string,
    userId: string
  ): Observable<any> {
    const url = `${this.baseUrl}/admin/user/journey-sections/status/${userId}`;
    const postObject = { sectionId, status };

    return this.http.post(url, postObject, { withCredentials: true });
  }

  getAdminDonorJourneySectionsForUser(userId: string) {
    const url = `${this.baseUrl}/admin/user/journey-sections/${userId}`;
    return this.http.get<DonorJourneySection[]>(url, { withCredentials: true });
  }

  getDonorAndRecipientProfileInfo(userId: string): Observable<ProfileInfo> {
    // const cachedData = this.getCachedData(this.profileCacheKey);

    // if (cachedData) {
    //   return of(cachedData); // Return cached data as an observable
    // }

    // const url = `${this.baseUrl}/admin/profile/info/${userId}`;

    // return this.http.get<any>(url).pipe(
    //   tap((response) => {
    //     this.cacheData(response, 15 * 60 * 1000, this.profileCacheKey);
    //   })
    // );

    const url = `${this.baseUrl}/admin/profile/info/${userId}`;

    return this.http.get<ProfileInfo>(url, { withCredentials: true });
  }

  getDonorProfile(): Observable<ProfileInfo> {
    // const cachedData = this.getCachedData(this.donorProfileCacheKey);

    // if (cachedData) {
    //   return of(cachedData);
    // }

    const url = `${this.baseUrl}/profile/donor/info`;
    return this.http.get<ProfileInfo>(url, { withCredentials: true });

    // return this.http.get<ProfileInfo>(url, { withCredentials: true }).pipe(
    //   tap((response) => {
    //     this.cacheData(
    //       response,
    //       this.donorProfileCacheDuration,
    //       this.donorProfileCacheKey
    //     );
    //   })
    // );
  }

  updateDonoranddRecipientProfileInfo(donorData: ProfileInfo) {
    const donorUrl = `${this.baseUrl}/profile/donor`;
    const recipientUrl = `${this.baseUrl}/profile/recipient`;

    const updateDonorProfile$ = this.http.post(donorUrl, donorData.data.donor);

    if (donorData.data.recipient) {
      const recipientPayload = this.trimRecipientNames(donorData.data.recipient);
      const updateRecipientProfile$ = this.http.post(
        recipientUrl,
        recipientPayload
      );

      return forkJoin([updateDonorProfile$, updateRecipientProfile$]).pipe(
        tap(() => {
          this.profileUpdatedSource.next(); // Notify subscribers
        })
      );
    }

    return updateDonorProfile$.pipe(
      tap(() => {
        this.profileUpdatedSource.next(); // Notify subscribers
      })
    );
  }

  updateDonorLocale(locale: string): Observable<any> {
    const url = `${this.baseUrl}/profile/donor/locale`;
    return this.http.patch(url, { locale }, { withCredentials: true }).pipe(
      tap(() => {
        this.profileUpdatedSource.next();
      })
    );
  }

  translateText(payload: TranslationRequest): Observable<TranslationResponse> {
    const url = `${environment.questionnaireApiUrl}/translations`;
    return this.http.post<TranslationResponse>(url, payload, {
      withCredentials: true,
    });
  }

  updateDonorProfilePartial(
    donorData: Partial<DonorProfile>
  ): Observable<any> {
    const url = `${this.baseUrl}/profile/donor`;
    return this.http.patch(url, donorData, { withCredentials: true }).pipe(
      tap(() => {
        this.profileUpdatedSource.next();
      })
    );
  }

  updateDonorProfileInfoByAdmin(userId: string, donorData: DonorProfile): Observable<any> {
    if (userId) {
      const recipientUrl = `${this.baseUrl}/admin/profile/${userId}/donor`;
      return this.http.put(recipientUrl, donorData).pipe(
        tap(() => {
          this.profileUpdatedSource.next(); // Notify subscribers
        })
      );
    }
    return of({});
  }


  updateRecipientProfileInfoByAdmin(recipientData: any): Observable<any> {
    if (recipientData.donorUserId) {
      const recipientUrl = `${this.baseUrl}/admin/profile/${recipientData.donorUserId}/recipient`;
      const recipientPayload = this.trimRecipientNames(recipientData);
      return this.http.put(recipientUrl, recipientPayload).pipe(
        tap(() => {
          this.profileUpdatedSource.next(); // Notify subscribers
        })
      );
    }
    return of({});
  }

  private trimRecipientNames<T extends { firstName?: string; lastName?: string }>(recipient: T): T {
    if (!recipient) return recipient;
    const trimmed = { ...recipient } as T;
    if (typeof trimmed.firstName === "string") {
      trimmed.firstName = trimmed.firstName.trim();
    }
    if (typeof trimmed.lastName === "string") {
      trimmed.lastName = trimmed.lastName.trim();
    }
    return trimmed;
  }

  updateDonorAssignment(donorId: string, assignedToId: string) {
    const updateAssignmentUrl = `${this.baseUrl}/admin/profile/assign/${donorId}`;
    const updateObject = {
      assignedTo: assignedToId,
    };
    return this.http.put(updateAssignmentUrl, updateObject);
  }



  addProviderToSection(practitionerId: string, userJourneySectionId: string){
    const addProviderToSectionUrl = `${this.baseUrl}/admin/user/journey-section/practitioner`;
    const postObject = {
      userJourneySectionId,
      practitionerId,
    }
    return this.http.post<any>(addProviderToSectionUrl, postObject);
  }

   updateProviderInSection(linkId: string, practitionerId: string, userJourneySectionId: string){
    const addProviderToSectionUrl = `${this.baseUrl}/admin/user/journey-section/practitioner/${linkId}`;
    const postObject = {
      userJourneySectionId,
      practitionerId,
    }
    return this.http.put<any>(addProviderToSectionUrl, postObject);
  }

  removeProviderFromSection(linkId: string){
    const removeProviderToSectionUrl = `${this.baseUrl}/admin/user/journey-section/practitioner/${linkId}`;
    return this.http.delete<any>(removeProviderToSectionUrl);
  }

  saveAppointment(appointmentInfo: Appointment, appointmentId?: string) {
    if (appointmentId && appointmentId.length > 0) {
      const updateAppointmentUrl = `${this.baseUrl}/admin/appointments/${appointmentId}`;
      return this.http.put<any>(updateAppointmentUrl, appointmentInfo, {
        withCredentials: true,
      });
    } else {
      const saveAppointmentUrl = `${this.baseUrl}/admin/appointments`;
      return this.http.post<any>(saveAppointmentUrl, appointmentInfo, {
        withCredentials: true,
      });
    }
  }

  deleteAppointment(appointmentId: string) {
    const url = `${this.baseUrl}/admin/appointments/${appointmentId}`;
    return this.http.delete(url);
  }

  getAppointmentsBySectionIdAndUserId(sectionId: string, userId: string) {
    const url = `${this.baseUrl}/admin/appointments/section/${sectionId}/user/${userId}`;
    return this.http.get<{ appointments: Appointment[] }>(url);
  }

  generateUploadUrl(fileName: string, contentType: string) {
    const url = `${this.baseUrl}/files/upload-url`;
    const postObject = { fileName, contentType };
    return this.http.post<{ uploadUrl: string }>(url, postObject, {
      withCredentials: true,
    });
  }

  uploadFileToS3(uploadUrl: string, file: File): Observable<any> {
    return this.http.put(uploadUrl, file, {
      headers: { "Content-Type": file.type }, // Required for S3 to set correct MIME type
      reportProgress: true, // Enable progress tracking (optional)
      observe: "events", // Allows listening to upload progress
    });
  }

  saveMetaData({
    sectionId,
    userJourneyStatusId,
    uploadUserId,
    s3Url,
    fileName,
    fileType,
    fileSize,
  }: {
    sectionId: string;
    userJourneyStatusId: string;
    uploadUserId: string;
    s3Url: string;
    fileName: string;
    fileType: string;
    fileSize: number;
  }) {
    const url = `${this.baseUrl}/files/save-metadata`;
    const postObject = {
      sectionId,
      userJourneyStatusId,
      uploadUserId,
      s3Url,
      fileName,
      fileType,
      fileSize,
    };
    return this.http.post(url, postObject, { withCredentials: true });
  }

  generateDownloadUrl(fileName: string, folderName?: string) {
    let url = `${this.baseUrl}/download/download-url?fileName=${encodeURIComponent(fileName)}`;

    if (folderName) {
      url += `&folderName=${encodeURIComponent(folderName)}`;
    }
    return this.http.get<{ downloadUrl: string }>(url, {
      withCredentials: true,
    });
  }

  deleteDocument(documentId: string) {
    const url = `${this.baseUrl}/files/${documentId}`;
    return this.http.delete(url, { withCredentials: true });
  }

  getDocumentsByUserJourneySection(userJourneySectionId: string) {
    const url = `${this.baseUrl}/files/user-journey/${userJourneySectionId}`;
    return this.http.get<S3Document[]>(url, { withCredentials: true });
  }

  updateShareWithDonor(shareWithDonor: boolean, documentId: string) {
    const url = `${this.baseUrl}/files/${documentId}/share`;
    const postObject = { shareWithDonor };
    return this.http.put(url, postObject, { withCredentials: true });
  }

  initiateConsentRequest(userName: string) {
    const url = `${this.baseUrl}/esign/contract`;
    const postObject = {
      name: `${userName}`,
      title: "Graft Hospital Living Donor Consent",
    };
    return this.http.post(url, postObject);
  }

  getContract() {
    const url = `${this.baseUrl}/esign/contract`;
    return this.http.get<Contract>(url);
  }

  getContractForAdminByUserId(userId: string) {
    const url = `${this.baseUrl}/admin/esign/contract/${userId}`;
    return this.http.get<Contract>(url);
  }

  getDonorRecommendations(recipientFirstName: string, recipientLastName: string, recipientDOB: string) {
    // Normalize recipientDOB to YYYY-MM-DD if possible
    if (recipientDOB) {
      let parts;
      if (/^\d{4}-\d{2}-\d{2}$/.test(recipientDOB)) {
        // already in correct format
      } else if (/^\d{2}[\/-]\d{2}[\/-]\d{4}$/.test(recipientDOB)) {
        parts = recipientDOB.split(/[\/-]/);
        recipientDOB = `${parts[2]}-${parts[0].padStart(2, '0')}-${parts[1].padStart(2, '0')}`;
      }
    }
    const url = `${this.baseUrl}/admin/recommendations/recipient?recipientFirstName=${encodeURIComponent(
      recipientFirstName
    )}&recipientLastName=${encodeURIComponent(recipientLastName)}&recipientDOB=${encodeURIComponent(recipientDOB)}`;

    return this.http.get<any>(url);
  }

  // Cache data with a timestamp
  private cacheData(data: any, ttl: number, cacheKey: string): void {
    const cacheEntry = {
      data,
      expiry: Date.now() + ttl, // Set expiration timestamp
    };
    localStorage.setItem(cacheKey, JSON.stringify(cacheEntry));
  }

  // Retrieve cached data if it's not expired
  private getCachedData(cacheKey: string): any {
    const cacheEntryString = localStorage.getItem(cacheKey);
    if (!cacheEntryString) {
      return null; // No cache entry
    }

    const cacheEntry = JSON.parse(cacheEntryString);
    if (Date.now() > cacheEntry.expiry) {
      localStorage.removeItem(cacheKey); // Remove expired cache
      return null;
    }

    return cacheEntry.data;
  }
  
  evaluateMetric<T extends string | number | [number, number]>(
    value: number | undefined,
    rule: T | { range: [number, number], yellow_constant?: number },
    field: string,
    redList: string[],
    yellowList: string[]
  ) {
    if (value === undefined || value === null || (typeof value === 'number' && isNaN(value))) return;

    if (typeof rule === 'object' && 'range' in rule) {
      const [min, max] = rule.range;
      const yellowMargin = rule.yellow_constant ?? 1;

      if (value < min || value > max) {
        redList.push(field);
      } else if (Math.abs(value - min) <= yellowMargin || Math.abs(value - max) <= yellowMargin) {
        yellowList.push(field);
      }

    } else if (Array.isArray(rule)) {
      const [min, max] = rule;
      if (value < min || value > max) {
        redList.push(field);
      } else if (Math.abs(value - min) <= 1 || Math.abs(value - max) <= 1) {
        yellowList.push(field);
      }

    } else if (typeof rule === 'number') {
      if (value < rule) {
        redList.push(field);
      } else if (value <= rule + 2) {
        yellowList.push(field);
      }

    } else {
      if (value != rule) {
        redList.push(field);
      }
    }
  }

}
